/**
 * Persist saved recipes in localStorage (scoped by storage key).
 * In-memory list is the source of truth; subscribers get notified on change.
 */

const MAX_RECIPES = 50;

function slugify(title) {
  const slug = String(title ?? 'recipe')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/gi, '-')
    .replace(/^-+|-+$/g, '');
  return slug || 'recipe';
}

/**
 * @param {string} storageKey – e.g. origin + partner id
 */
export function createRecipeStore(storageKey) {
  let recipes = [];
  const listeners = new Set();

  function _notify() {
    const snapshot = recipes.slice();
    listeners.forEach((fn) => {
      try { fn(snapshot); } catch (e) { /* subscriber */ }
    });
  }

  function _load() {
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) recipes = parsed;
    } catch (e) {
      console.warn('[RecipeStore] Could not load recipes:', e);
    }
  }

  function _persist() {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(recipes));
    } catch (e) {
      console.warn('[RecipeStore] Could not persist recipes:', e);
    }
  }

  function getAll() {
    return recipes.slice();
  }

  function add(recipe) {
    if (!recipe || !recipe.title) return null;
    const entry = {
      id: `${slugify(recipe.title)}-${Date.now()}`,
      title: recipe.title,
      ingredients: Array.isArray(recipe.ingredients) ? recipe.ingredients.slice() : [],
      steps: Array.isArray(recipe.steps) ? recipe.steps.slice() : [],
      servings: recipe.servings || '',
      notes: recipe.notes || '',
      savedAt: Date.now(),
    };
    recipes.unshift(entry);
    if (recipes.length > MAX_RECIPES) recipes.length = MAX_RECIPES;
    _persist();
    _notify();
    return entry;
  }

  function subscribe(fn) {
    listeners.add(fn);
    return () => listeners.delete(fn);
  }

  _load();
  return { getAll, add, subscribe, count: () => recipes.length };
}

/**
 * Build a scoped storage key for the current page + partner.
 * @param {string} [partnerId]
 */
export function recipeStorageKey(partnerId = 'default') {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  return `${origin}${path}_ttp_recipes_${partnerId}`;
}
